import { AuthController } from '@/business/modules/auth';
import cookie from '@/presentation/libs/cookie.lib';
import useSharedStore from '@/presentation/store/shared';

const actions = {
	// TODO: Resolve circular dependency of AuthController
	/**
	 * Creates a new AuthController instance when called.
	 * This method is used to avoid circular dependency issues that would occur
	 * if we instantiated AuthController at the top of the file. By deferring
	 * the instantiation to method calls, we break the dependency chain that
	 * would form if AuthController has dependencies that eventually reference
	 * this store module.
	 */
	getInstance() {
		return new AuthController();
	},

	setPhoneNumber(phoneNumber) {
		this.phoneNumber = phoneNumber;
	},

	setOtpFlow(flow) {
		this.otpFlow = flow;
	},

	setPendingSignupPayload(payload) {
		this.pendingSignupPayload = payload;
	},

	setAdminTotpChallengeToken(challengeToken) {
		this.adminTotpChallengeToken = challengeToken;
	},

	clearPendingSignupPayload() {
		this.pendingSignupPayload = null;
		this.otpFlow = 'login';
	},

	async loginInit(phoneNumber) {
		const response = await this.getInstance().loginInit(phoneNumber);
		this.setAvailableContexts(response);
		return response;
	},

	loginInitForReservation(phoneNumber) {
		return this.getInstance().loginInitForReservation(phoneNumber);
	},

	sendOtp(phoneNumber) {
		return this.getInstance().loginInit(phoneNumber);
	},

	loginAsAdmin(otpCode) {
		return this.getInstance().loginAsAdmin(otpCode);
	},

	verifyAdminTotpLogin(payload) {
		return this.getInstance().verifyAdminTotpLogin(payload);
	},

	verifyAdminRecoveryCodeLogin(payload) {
		return this.getInstance().verifyAdminRecoveryCodeLogin(payload);
	},

	verifyOtp(otp) {
		return this.getInstance().verifyOtp(otp);
	},

	signup(data) {
		return this.getInstance().signup(data);
	},

	signupForReservation(data) {
		return this.getInstance().signupForReservation(data);
	},

	updateUserInformation(payload) {
		return this.getInstance().updateUserInformation(payload);
	},

	getUserInformation() {
		return this.getInstance().getUserInformation();
	},

	setAvailableContexts(payload) {
		const response = payload?.data ?? payload;
		const rawContexts = response?.contexts ?? response?.Contexts ?? response;
		if (!Array.isArray(rawContexts)) return;

		this.availableContexts = rawContexts
			.map((context) => ({
				type: String(context?.type ?? context?.Type ?? '').trim(),
				id: String(context?.id ?? context?.Id ?? '').trim(),
				name: String(context?.name ?? context?.Name ?? '').trim(),
			}))
			.filter((context) => context.type && context.id);
	},

	async loadAvailableContexts() {
		const response = await this.getInstance().getAvailableContexts();
		this.setAvailableContexts(response);
		return this.availableContexts;
	},

	loginByRefreshToken(token) {
		return this.getInstance().loginByRefreshToken(token);
	},

	logout() {
		return this.getInstance().logout();
	},

	switchContext(payload) {
		return this.getInstance().switchContext(payload);
	},

	async switchUserContext(payload) {
		const response = await this.getInstance().switchContext(payload);
		const tokens = response?.jwtTokens ?? response;
		const accessToken = tokens?.accessToken || tokens?.AccessToken;
		const refreshToken = tokens?.refreshToken || tokens?.RefreshToken;

		if (accessToken) {
			cookie.set('access-token', accessToken);
			if (refreshToken) {
				cookie.set('refresh-token', refreshToken);
			}
			const sharedStore = useSharedStore();
			sharedStore.setUserInformationOfToken(accessToken);
		}

		return response;
	},
};

export default actions;
